
import { getCompressedImageUrl, generateSrcSet } from './imageCompression';

/**
 * Preloads a page by fetching its HTML content
 * @param {string} url - The URL to preload
 */
export const preloadPage = (url) => {
  // Skip if URL is the current page or if it's an external URL
  if (url === window.location.pathname || url.startsWith('http') || url.startsWith('#')) {
    return;
  }
  
  // Create a prefetch link element
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = url;
  link.as = 'document';
  
  // Add the link to the head
  document.head.appendChild(link);
  
  console.log(`Preloaded: ${url}`);
};

/**
 * Efficiently preloads an image with optimized settings
 * Uses modern browser optimizations and compression
 * 
 * @param {string} src - The image URL to preload
 * @param {object} compressionOptions - Options for image compression
 * @returns {Promise} - A promise that resolves when the image is loaded
 */
export const preloadImage = (src, compressionOptions = {}) => {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('No image source provided'));
      return;
    }

    const img = new Image();
    
    // Enable browser's native image optimization features
    img.decoding = 'async';
    img.fetchPriority = 'high';
    
    // Set up srcset for responsive loading if applicable
    const srcset = generateSrcSet(src);
    if (srcset) {
      img.srcset = srcset;
    }
    
    img.onload = () => {
      console.log(`Image loaded: ${src}`);
      resolve(img);
    };
    
    img.onerror = (err) => {
      console.error(`Failed to load image: ${src}`, err);
      reject(err);
    };
    
    // Apply compression to the image URL
    img.src = getCompressedImageUrl(src, compressionOptions);
    
    // For critical images, store in browser cache
    if (compressionOptions.critical) {
      // Add to browser cache via localStorage for future visits
      try {
        localStorage.setItem(`preloaded_${src}`, new Date().toISOString());
      } catch (e) {
        // Ignore storage errors
      }
    }
  });
};

/**
 * Preload multiple images at once with priority management
 * 
 * @param {Array} srcs - Array of image URLs to preload
 * @param {object} options - Preload options
 * @returns {Promise} - Promise that resolves when all images are loaded
 */
export const preloadImages = (srcs, options = {}) => {
  if (!srcs || !srcs.length) return Promise.resolve([]);
  
  const { concurrency = 4, compressionOptions = {} } = options;
  
  // Load images in batches to avoid overwhelming the browser
  const batchLoad = (batch) => {
    return Promise.all(
      batch.map(src => preloadImage(src, compressionOptions))
    );
  };
  
  // Break array into smaller chunks for controlled loading
  const chunks = [];
  for (let i = 0; i < srcs.length; i += concurrency) {
    chunks.push(srcs.slice(i, i + concurrency));
  }
  
  // Process chunks sequentially
  return chunks.reduce(
    (promise, chunk) => promise.then(() => batchLoad(chunk)),
    Promise.resolve()
  );
};
